import { Card, Frame, Inline } from 'almanac-ui/icons'
import { Node as ProseNode } from 'prosemirror-model'
import { NodeSelection } from 'prosemirror-state'
import { type EditorView } from 'prosemirror-view'
import { createMentionSmartLink } from './smartLinks/utils'

export const handleChangeToCardWithMode = (node: ProseNode, pos: number, view: EditorView, mode: 'card' | 'frame') => {
  const { tr } = view.state

  if (node.type.name === 'smartLink') {
    const nodeContent = node.content?.content
    const commentUuids: string[] =
      nodeContent &&
      nodeContent.flatMap((content) => {
        return content.marks.filter((mark) => mark.type.name === 'comment').map((mark) => mark.attrs.uuid)
      })
    const taskUuids: string[] =
      nodeContent &&
      nodeContent.flatMap((content) => {
        return content.marks.filter((mark) => mark.type.name === 'task').map((mark) => mark.attrs.uuid)
      })
    // Smartlinks are inline nodes, if they are at the beginning of a paragraph we need to start delete from there
    const possibleParagraphPos = pos - 1
    const possibleParagraphNode = tr.doc.nodeAt(possibleParagraphPos)
    if (possibleParagraphNode?.type.name === 'paragraph') {
      pos -= 1
      tr.delete(pos, pos + 1 + node.nodeSize)
    } else {
      tr.delete(pos, pos + node.nodeSize)
    }

    tr.insert(
      pos,
      view.state.schema.nodes.card.create({
        ...node.attrs,
        mode,
        mentionType: node.attrs.type,
        uuids: commentUuids,
        taskUuids,
      }),
    )
  } else if (node.type.name === 'card') {
    tr.setNodeMarkup(pos, undefined, {
      ...node.attrs,
      mode,
    })
  } else {
    throw new Error('Unexpected node type')
  }

  const $cardPos = tr.doc.resolve(pos).nodeAfter?.type.name === 'card' ? tr.doc.resolve(pos) : tr.doc.resolve(pos + 1)

  if ($cardPos.nodeAfter?.type.name === 'card') {
    tr.setSelection(new NodeSelection($cardPos))
  }
  view.dispatch(tr)
  view.focus()
}

export const handleChangeToInlineView = (node: ProseNode, pos: number, view: EditorView, title: string) => {
  const { mentionType, key, userId, uuids, taskUuids, url } = node.attrs

  const attributes = { type: mentionType, url, key, title, userId }
  const newNode = createMentionSmartLink(view, attributes)
  const { tr } = view.state
  tr.delete(pos, pos + node.nodeSize)
  tr.insert(pos, newNode)
  uuids.forEach((uuid: string) => {
    const mark = view.state.schema.marks.comment.create({
      uuid,
    })
    tr.addMark(pos, pos + newNode.nodeSize, mark)
  })
  taskUuids.forEach((uuid: string) => {
    const mark = view.state.schema.marks.task.create({
      uuid,
    })
    tr.addMark(pos, pos + newNode.nodeSize, mark)
  })

  // Inline view is inside a paragraph, so move 1 in when re-selecting node
  tr.setSelection(NodeSelection.create(tr.doc, pos + 1))
  view.dispatch(tr)
  view.focus()
}

export const mentionsConversionOptions = (
  node: ProseNode,
  pos: number,
  view: EditorView,
  title: string,
): { [key: string]: { disabled: boolean; label: string; changeFn: () => void; icon: () => JSX.Element } } => ({
  inline: {
    disabled: false,
    label: 'Inline',
    changeFn: () => handleChangeToInlineView(node, pos, view, title),
    icon: () => <Inline />,
  },
  card: {
    disabled: false,
    label: 'Card',
    changeFn: () => handleChangeToCardWithMode(node, pos, view, 'card'),
    icon: () => <Card />,
  },
  frame: {
    disabled: false,
    label: 'Frame',
    changeFn: () => handleChangeToCardWithMode(node, pos, view, 'frame'),
    icon: () => <Frame />,
  },
})
